import { RecipeIngredient } from '@/api/types'

const zeroedNutritionalValueFields = {
  doseGr: 0,
  energy: 0,
  carbohydrate: 0,
  cholesterol: 0,
  fat: 0,
  fiber: 0,
  protein: 0,
  water: 0,
  alcohol: 0,
  starch: 0,
  sugar: 0,
  salt: 0,
  // fatty acids
  saturatedFattyAcids: 0,
  monoUnsaturatedFattyAcids: 0,
  polyUnsaturatedFattyAcids: 0,
  transFattyAcids: 0,
  // vitamins
  vitaminC: 0,
  thiamin: 0,
  riboFlavin: 0,
  niacin: 0,
  vitaminB6: 0,
  folate: 0,
  vitaminB12: 0,
  vitaminA: 0,
  vitaminE: 0,
  vitaminD: 0,
  // minerals
  calcium: 0,
  iron: 0,
  magnesium: 0,
  phosphorus: 0,
  potassium: 0,
  zinc: 0,
  sodium: 0,
}

const nutrientToFactorConstituent = {
  carbohydrate: 'CARBOHYDRATE',
  cholesterol: 'STEROLS',
  fat: 'FAT',
  fiber: 'DIETARY_FIBRE',
  protein: 'PROTEIN',
  water: '',
  alcohol: '',
  starch: 'CARBOHYDRATE',
  sugar: 'CARBOHYDRATE',
  salt: 'SODIUM',
  // fatty acids
  saturatedFattyAcids: 'FAT',
  monoUnsaturatedFattyAcids: 'FAT',
  polyUnsaturatedFattyAcids: 'FAT',
  transFattyAcids: '',
  // vitamins
  vitaminC: 'VITAMIN_C',
  thiamin: 'VITAMIN_B1',
  riboFlavin: 'VITAMIN_B2',
  niacin: 'NIACIN',
  vitaminB6: 'VITAMIN_B6',
  folate: 'FOLATE',
  vitaminB12: 'VITAMIN_B12',
  vitaminA: 'RETINOL',
  vitaminE: 'VITAMIN_E',
  vitaminD: 'VITAMIN_D',
  // minerals
  calcium: 'CALCIUM',
  iron: 'IRON',
  magnesium: 'MAGNESIUM',
  phosphorus: 'PHOSPHOR',
  potassium: 'POTASSIUM',
  zinc: 'ZINC',
  sodium: 'SODIUM',
}

const nutritionalValuesToRecipeNutritionalInfoAttribute = {
  carbohydrate: 'carbohydrate',
  energy: 'energyKcal',
  fat: 'fat',
  fiber: 'fiber',
  protein: 'protein',
  salt: 'salt',
  saturatedFattyAcids: 'saturatedFat',
  sugar: 'sugar',
}

export type NutritionalValueFields = typeof zeroedNutritionalValueFields

export default {
  fromMealPlans: function (mealPlans): NutritionalValueFields {
    const _mps = mealPlans

    // merge all recipeIngredients from the mealPlans
    return this.fromRecipes(
      _mps.map((mealPlan) => mealPlan.scaledRecipes).reduce((a, b) => a.concat(b), []),
    )
  },

  fromRecipes: function (recipes /*: ScaledRecipe or Recipe */): NutritionalValueFields {
    return recipes
      .map((recipe) =>
        recipe.recipe?.recipeNutritionalInfo || recipe.recipeNutritionalInfo
          ? this.fromRecipeNutritionalInfo(
              recipe.recipe?.recipeNutritionalInfo || recipe.recipeNutritionalInfo,
            )
          : this.fromRecipeIngredients(recipe.scaledRecipeIngredients || recipe.recipeIngredients),
      )
      .reduce(
        (a, b) => {
          const merged = {}
          Object.keys(zeroedNutritionalValueFields).forEach(
            (key) => (merged[key] = a[key] + b[key]),
          )
          return merged
        },
        Object.assign({}, zeroedNutritionalValueFields),
      )
  },

  fromRecipeNutritionalInfo(recipeNutritionalInfo): NutritionalValueFields {
    const _nv = Object.assign({}, zeroedNutritionalValueFields)

    for (const f in _nv) {
      const mappedAttribute = nutritionalValuesToRecipeNutritionalInfoAttribute[f]
      const _val = mappedAttribute && recipeNutritionalInfo[mappedAttribute]
      _nv[f] = _val * 1.0
    }

    return _nv
  },

  fromRecipeIngredients: function (recipeIngredients, servings = 1): NutritionalValueFields {
    const _nv = Object.assign({}, zeroedNutritionalValueFields)
    servings = servings || 1

    recipeIngredients
      .filter((ri) => ri.ingredient && ri.quantity > 0)
      .forEach((ri: RecipeIngredient) => {
        for (const f in _nv) {
          const _i = ri.ingredient
          let _val

          const _weightFactor = this._getWeightYieldFactor(ri)
          const _nutrientRetentionFactor = this._getNutrientRetentionFactor(ri, f)
          const _unitsFactor = ri?.unit?.quantityInGrams ?? 1

          if (typeof _i[f] !== 'undefined') {
            _val = _i[f]
          } else if (typeof (_i.fattyAcids && _i.fattyAcids[f]) !== 'undefined') {
            _val = _i.fattyAcids[f]
          } else if (typeof (_i.vitamins && _i.vitamins[f]) !== 'undefined') {
            _val = _i.vitamins[f]
          } else if (typeof (_i.minerals && _i.minerals[f]) !== 'undefined') {
            _val = _i.minerals[f]
          }
          _nv[f] +=
            (ri.quantity * _unitsFactor * _weightFactor * _nutrientRetentionFactor * (_val / 100)) /
            servings
        }
      })

    if (isNaN(_nv.energy) && !isNaN(_nv.protein) && !isNaN(_nv.carbohydrate) && !isNaN(_nv.fat)) {
      _nv.energy =
        (_nv.protein * 4 + _nv.carbohydrate * 4 + _nv.fat * 9 + _nv.alcohol * 7) / servings
    }
    return _nv
  },

  _getWeightYieldFactor: (recipeIngredient) => {
    if (recipeIngredient.weightYieldFactor) {
      return recipeIngredient.weightYieldFactor.ediblePartMean
    }
    return 1.0
  },

  _getNutrientRetentionFactor: (recipeIngredient, nutrient) => {
    const constituent = nutrientToFactorConstituent[nutrient]
    if (
      recipeIngredient.nutrientRetentionFactors &&
      constituent &&
      recipeIngredient.nutrientRetentionFactors.retentionFactors[constituent] !== undefined
    ) {
      if (constituent === 'FAT') {
        const uptake = recipeIngredient.nutrientRetentionFactors.retentionFactors.FAT_UPTAKE
          ? recipeIngredient.nutrientRetentionFactors.retentionFactors.FAT_UPTAKE
          : 0.0
        return recipeIngredient.nutrientRetentionFactors.retentionFactors[constituent] + uptake
      } else {
        return recipeIngredient.nutrientRetentionFactors.retentionFactors[constituent]
      }
    }
    return 1.0
  },
}
